/**
 * BetaTestingBanner — dismissible banner shown to authenticated beta
 * users on the Dashboard. Reappears 14 days after dismissal (cooldown
 * enforced server-side so it persists across devices).
 *
 * Placement: between EmailVerificationBanner and HealthAlertBanner on
 * Dashboard.jsx. Hidden if the user dismissed it within the last 14 days.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Rocket, X } from 'lucide-react';
import { useBetaFeedback } from '@/contexts/BetaFeedbackContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SURFACE = 'beta_banner';

const BetaTestingBanner = () => {
  const { openFeedbackModal, trackEvent } = useBetaFeedback();
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState(false);

  // Ask the server whether to show — handles the 14-day cooldown rule
  // centrally so dismissal sticks across devices.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/feedback/banner-state`, { withCredentials: true });
        if (cancelled) return;
        const show = res.data?.show !== false;
        setVisible(show);
        if (show) trackEvent('impression', SURFACE);
      } catch {
        // Default to NOT showing if we can't reach the server — better
        // to under-promote than to nag during an outage.
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = async () => {
    setVisible(false);
    trackEvent('dismiss', SURFACE);
    try {
      await axios.post(`${API}/feedback/banner-dismiss`, {}, { withCredentials: true });
    } catch { /* silent — UI already hidden */ }
  };

  if (!checked || !visible) return null;

  return (
    <div
      className="rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3 sm:py-3.5 shadow-sm mb-3"
      role="status"
      aria-live="polite"
      data-testid="beta-testing-banner"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-white/20 flex-shrink-0">
          <Rocket className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">🚀 You&apos;re helping shape HealthLoop.</p>
          <p className="text-xs sm:text-sm mt-0.5 opacity-95">
            We welcome your feedback as we improve the platform.
          </p>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2 bg-white text-emerald-700 hover:bg-emerald-50"
            onClick={() => openFeedbackModal(SURFACE)}
            data-testid="beta-banner-cta-btn"
          >
            Give Feedback
          </Button>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss beta banner"
          className="p-1 rounded-md hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/40 flex-shrink-0"
          data-testid="beta-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default BetaTestingBanner;
